/**
 * Restart announcer — surfaces dashboard restarts in the Awareness activity feed.
 *
 * Restart writers (the supervisor watchdog, `pan reload`, `pan restart`) run in
 * separate processes and cannot reach the dashboard's in-process event store —
 * and mid-restart the dashboard is the very thing being killed. So the
 * (re)started server announces instead: it reads
 * `~/.overdeck/restart-status.json` on boot and polls it afterward, emitting
 * one activity entry per new restart-status `ts`. Polling also catches the
 * boot race (the watchdog writes the status a few seconds AFTER the new server
 * is already up) and watchdog give-ups that never restarted anything.
 *
 * The last announced `ts` is persisted in app-settings so a later boot doesn't
 * re-announce, and entries older than ANNOUNCE_MAX_AGE_MS are recorded without
 * being announced — a restart from hours ago is history, not news.
 */
import { Effect } from 'effect';
import {
  emitActivityEntrySync,
  type EmitActivityOptions,
} from '../../../lib/activity-logger.js';
import { readRestartStatus, writeRestartStatus, type RestartStatus } from '../../../lib/restart-status.js';
import { getSetting, setSetting } from '../../../lib/overdeck/control-settings.js';
import { getConversationByTmuxSession } from '../../../lib/overdeck/conversations.js';
import { isSupervisorUnitFailed, SUPERVISOR_UNIT_NAME } from '../../../lib/systemd.js';

export const RESTART_ANNOUNCER_LAST_TS_KEY = 'restart_announcer.last_announced_ts';
const POLL_MS = 15_000;
const BOOTSTRAP_POLL_MS = 1_000;
const BOOTSTRAP_FAST_POLL_COUNT = 30;
const ANNOUNCE_MAX_AGE_MS = 60 * 60_000;
const SUPERVISOR_UNIT_FAILED_REASON = 'supervisor-unit-failed';
let supervisorUnitFailureEpisodeActive = false;

/** Minimal conversation shape the announcer needs from the initiator's tmux session. */
export type InitiatorConversationResolver = (tmuxSession: string) => { id: number; title?: string | null } | null;

/** Default resolver: the conversations DB, keyed by the initiator's tmux session name. */
function resolveInitiatorConversation(tmuxSession: string): { id: number; title?: string | null } | null {
  try {
    return getConversationByTmuxSession(tmuxSession);
  } catch {
    return null;
  }
}

/** Dashboard route for the restart's initiator: flywheel orchestrator → the
 *  flywheel page, conversations → their conversation page. Issue agents route
 *  via the entry's issueId instead (same destination as the issue tree).
 *  The initiator string is a TMUX SESSION NAME (`conv-20260610-4123`), not a
 *  conversation id — `/conv/:key` only resolves numeric ids, so the link must
 *  go through the conversations DB (a name-based link landed on an empty
 *  Command Deck). */
export function initiatorLink(
  initiator: string | undefined,
  resolveConversation: InitiatorConversationResolver = resolveInitiatorConversation,
): string | undefined {
  if (!initiator) return undefined;
  if (initiator === 'conv-flywheel-orchestrator') return '/flywheel';
  if (initiator.startsWith('conv-')) {
    const conv = resolveConversation(initiator);
    return conv ? `/conv/${conv.id}` : undefined;
  }
  return undefined;
}

function describeInitiator(
  initiator: string | undefined,
  resolveConversation: InitiatorConversationResolver,
): string {
  if (!initiator) return '';
  if (initiator === 'conv-flywheel-orchestrator') return ' by the flywheel orchestrator';
  if (initiator.startsWith('conv-')) {
    const conv = resolveConversation(initiator);
    if (conv?.title) return ` by conversation ${conv.id} ("${conv.title}")`;
    if (conv) return ` by conversation ${conv.id}`;
    return ` by conversation ${initiator.slice('conv-'.length)}`;
  }
  return ` by ${initiator}`;
}

/** Map a restart-status entry to an activity-feed entry. Pure; exported for tests. */
export function describeRestart(
  status: RestartStatus,
  resolveConversation: InitiatorConversationResolver = resolveInitiatorConversation,
): EmitActivityOptions {
  const seconds = (status.durationMs / 1000).toFixed(1);
  if (status.trigger === 'watchdog') {
    if (status.gaveUp) {
      return {
        source: 'supervisor',
        level: 'error',
        message: `Supervisor watchdog GAVE UP restarting the dashboard after ${status.attempts} attempt(s) — manual intervention required`,
        details: status.error,
      };
    }
    if (!status.success) {
      return {
        source: 'supervisor',
        level: 'error',
        message: `Supervisor watchdog dashboard restart failed (attempt ${status.attempts})${status.reason ? ` — ${status.reason}` : ''}`,
        details: status.error,
      };
    }
    return {
      source: 'supervisor',
      level: 'warn',
      message: `Supervisor watchdog restarted the dashboard (attempt ${status.attempts}, ${seconds}s)${status.reason ? ` — ${status.reason}` : ''}`,
    };
  }
  const actor = describeInitiator(status.initiator, resolveConversation);
  return {
    source: 'dashboard',
    level: status.success ? 'info' : 'error',
    message: status.success
      ? `Dashboard restarted via ${status.trigger}${actor} (${seconds}s)`
      : `Dashboard restart via ${status.trigger}${actor} failed`,
    details: status.error,
    issueId: status.issueId,
    link: initiatorLink(status.initiator, resolveConversation),
  };
}

export interface RestartAnnouncerDeps {
  readStatus?: () => Promise<RestartStatus | null>;
  writeStatus?: (status: RestartStatus) => Promise<void>;
  readSupervisorUnitFailed?: () => Promise<boolean>;
  emit?: (options: EmitActivityOptions) => void;
  getLastAnnounced?: () => string | null;
  setLastAnnounced?: (ts: string) => void;
  now?: () => number;
}

function isSupervisorUnitFailureStatus(status: RestartStatus | null): status is RestartStatus {
  return status?.trigger === 'watchdog'
    && status.success === false
    && status.gaveUp === true
    && status.reason === SUPERVISOR_UNIT_FAILED_REASON;
}

function buildSupervisorUnitFailureStatus(ts: string): RestartStatus {
  return {
    ts,
    trigger: 'watchdog',
    success: false,
    durationMs: 0,
    attempts: 3,
    gaveUp: true,
    reason: SUPERVISOR_UNIT_FAILED_REASON,
    error: `${SUPERVISOR_UNIT_NAME} is in failed state; the systemd restart limit was reached and manual intervention is required`,
    pid: process.pid,
  };
}

async function syncSupervisorUnitFailureStatus(
  deps: Required<Pick<RestartAnnouncerDeps, 'readStatus' | 'writeStatus' | 'readSupervisorUnitFailed' | 'now'>>,
): Promise<void> {
  const failed = await deps.readSupervisorUnitFailed().catch(() => false);
  if (!failed) {
    supervisorUnitFailureEpisodeActive = false;
    return;
  }

  const current = await deps.readStatus();
  if (isSupervisorUnitFailureStatus(current)) {
    supervisorUnitFailureEpisodeActive = true;
    return;
  }

  if (supervisorUnitFailureEpisodeActive) return;

  await deps.writeStatus(buildSupervisorUnitFailureStatus(new Date(deps.now()).toISOString()));
  supervisorUnitFailureEpisodeActive = true;
}

/** One announce pass. Exported for tests. Returns true if an entry was emitted. */
export async function announceNewRestart(deps: RestartAnnouncerDeps = {}): Promise<boolean> {
  const readStatus = deps.readStatus
    ?? (() => Effect.runPromise(readRestartStatus()).catch(() => null));
  const writeStatus = deps.writeStatus
    ?? ((status: RestartStatus) => Effect.runPromise(writeRestartStatus(status)));
  const readSupervisorUnitFailed = deps.readSupervisorUnitFailed
    ?? (() => isSupervisorUnitFailed());
  const emit = deps.emit ?? emitActivityEntrySync;
  const getLastAnnounced = deps.getLastAnnounced
    ?? (() => getSetting(RESTART_ANNOUNCER_LAST_TS_KEY));
  const setLastAnnounced = deps.setLastAnnounced
    ?? ((ts: string) => setSetting(RESTART_ANNOUNCER_LAST_TS_KEY, ts));
  const now = deps.now ?? Date.now;

  await syncSupervisorUnitFailureStatus({ readStatus, writeStatus, readSupervisorUnitFailed, now });

  const status = await readStatus();
  if (!status) return false;
  if (getLastAnnounced() === status.ts) return false;

  const ageMs = now() - Date.parse(status.ts);
  if (!Number.isFinite(ageMs) || ageMs > ANNOUNCE_MAX_AGE_MS) {
    // Too old to announce, but mark as seen so we never retry this one.
    setLastAnnounced(status.ts);
    return false;
  }

  // Emit first — only mark as announced if the emit wasn't silently dropped
  // (e.g. event store not ready yet on boot). Next bootstrap poll will retry.
  emit(describeRestart(status));
  setLastAnnounced(status.ts);
  return true;
}

let timer: ReturnType<typeof setTimeout> | null = null;
let running = false;
let runToken = 0;

export function startRestartAnnouncer(deps: RestartAnnouncerDeps = {}): void {
  if (running) return;
  running = true;
  const token = ++runToken;
  let remainingBootstrapPolls = BOOTSTRAP_FAST_POLL_COUNT;

  const schedule = (delayMs: number) => {
    if (!running || token !== runToken) return;
    timer = setTimeout(pass, delayMs);
    timer.unref?.();
  };

  const nextDelay = (emitted: boolean): number => {
    if (emitted) return POLL_MS;
    if (remainingBootstrapPolls > 0) {
      remainingBootstrapPolls -= 1;
      return BOOTSTRAP_POLL_MS;
    }
    return POLL_MS;
  };

  const pass = () => {
    void announceNewRestart(deps)
      .then((emitted) => schedule(nextDelay(emitted)))
      .catch(() => schedule(nextDelay(false)));
  };

  pass();
}

export function stopRestartAnnouncer(): void {
  running = false;
  runToken += 1;
  if (timer) clearTimeout(timer);
  timer = null;
}
